import { initializeApp } from "firebase/app";
import type { FirebaseApp } from "firebase/app";
import {
  getMessaging,
  getToken as getMessagingToken,
  onMessage as onFirebaseMessage,
} from "firebase/messaging";
import type { Messaging } from "firebase/messaging";
import type { Unsubscribe } from "@firebase/util";
import { updateUserService } from "@/new_design/user/updateUser";
import type { NotificationRepository } from "../../domain/repositories/NotificationRepository";
import useAuthStore from "@/new_design/auth/stores/auth";

const firebaseConfig = {
  dev: {
    apiKey: "AIzaSyBZvCfxbr9jO5r7Y3gMzMo6KrdboieIBts",
    authDomain: "axishuberp.firebaseapp.com",
    projectId: "axishuberp",
    storageBucket: "axishuberp.firebasestorage.app",
    messagingSenderId: "168761645124",
    appId: "1:168761645124:web:b9601c50bd48a0c68f0411",
    measurementId: "G-PCTZL9V4NR",
  },
  prod: {
    apiKey: "AIzaSyBZvCfxbr9jO5r7Y3gMzMo6KrdboieIBts",
    authDomain: "axishuberp.firebaseapp.com",
    projectId: "axishuberp",
    storageBucket: "axishuberp.firebasestorage.app",
    messagingSenderId: "168761645124",
    appId: "1:168761645124:web:b9601c50bd48a0c68f0411",
    measurementId: "G-PCTZL9V4NR",
  },
};

const ENV =
  self.location.hostname === "localhost" ||
  self.location.hostname === "hub.dev.axishuberp.com"
    ? "dev"
    : "prod";

interface Profile {
  isActive: boolean;
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  company: string;
  user: {
    _id: string;
    email: string;
    roles: string[];
    isActive: boolean;
    lastLogin: string;
    temporaryPassword: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
    isDeleted: boolean;
    resetPasswordExpires: string;
    resetPasswordToken: string;
    id: string;
  };
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  firebaseNotificationId: string;
  firebaseNotificationType: string;
  mttoTablesConfig: {
    systemUpdate: string;
    tables: Array<{
      id: string;
      fields: Array<{
        name: string;
        value: string;
        visible: boolean;
        displayOn?: string[];
      }>;
      limit: number;
    }>;
    widgets: any[];
    toggles: Array<{
      name: string;
      value: boolean;
    }>;
    goals: Array<{
      name: string;
      value: number;
    }>;
    targets: Array<{
      tab: string;
      section: string;
      value: boolean;
    }>;
    tabsConfig: any[];
    tables_new: {
      planingLoadBulk: {
        fields: Array<{
          name: string;
          value: string;
          visible: boolean;
          tabs?: string[];
        }>;
        limit: number;
      };
    };
  };
  mainApp: string;
  avatar: string;
  fleetTypes: string[];
  fullName: string;
  id: string;
}

interface AuthResponse {
  profile: Profile;
  userId: string;
}

export class FirebaseNotificationRepository implements NotificationRepository {
  private static app: FirebaseApp;
  private static messaging: Messaging;
  private static initialized = false;

  constructor() {
    this.initializeFirebase();
  }

  private initializeFirebase() {
    if (!FirebaseNotificationRepository.initialized) {
      try {
        FirebaseNotificationRepository.app = initializeApp(firebaseConfig[ENV]);
        FirebaseNotificationRepository.messaging = getMessaging(
          FirebaseNotificationRepository.app
        );
        FirebaseNotificationRepository.initialized = true;
      } catch (error) {
        console.error("Error initializing Firebase:", error);
        throw new Error("Error initializing Firebase");
      }
    }
  }

  async getFirebaseToken(
    vapidKey: string,
    serviceWorkerRegistration: ServiceWorkerRegistration
  ): Promise<string> {
    try {
      if (!FirebaseNotificationRepository.initialized) {
        throw new Error("Firebase not initialized");
      }

      const token = await getMessagingToken(
        FirebaseNotificationRepository.messaging,
        {
          vapidKey,
          serviceWorkerRegistration, // 👈 Receive the registration from the store
        }
      );

      if (!token) throw new Error("Could not generate token");

      const { profile } = useAuthStore();
      if (!profile?.user?.roles) {
        throw new Error("User profile not available");
      }

      // 5. Update user using service (injected via composables)
      await updateUserService.updateCompanyUser(
        profile._id,
        {
          firebaseNotificationType: "WEB",
          firebaseNotificationId: token,
        },
        profile.user.roles[0]
      );

      return token;
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Error getting token: ${error.message}`);
      } else {
        throw new Error("Error getting token: unknown error");
      }
    }
  }

  onMessage(callback: (payload: any) => void): Unsubscribe {
    return onFirebaseMessage(
      FirebaseNotificationRepository.messaging,
      callback
    );
  }
}
